import {
  Card,
  Row,
  Table,
  Col,
  Input,
  message,
  Spin,
  Modal,
  Image,
  Switch,
  Tooltip,
  Button,
} from "antd";
import { AiFillDelete } from "react-icons/ai";
import { RiAddBoxFill } from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";
import { SearchOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { FaEdit, FaPen, FaTrashAlt } from "react-icons/fa";
import React from "react";
import { getCustomers, deleteCustomer, editCustomer } from "../../utils/API";
import { hasPermission } from "../../utils/HasPermission";
import AddCustomerModal from "./Add";
// import addcss from ""
import EditCustomersModal from "./Edit";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

interface Customer {
  _id: string;
  name: string;
  mobile: string;
  companyName: string;
  email: string;
  profilePhoto?: string;
  // add other fields as needed
}

export const CustomersTable = () => {
  const [customerData, setCustomerData] = useState<Customer | null>(null);
  const [dataSource, setDataSource] = useState<Customer[]>([]);
  const [filteredData, setFilteredData] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [currentTask, setCurrentTask] = useState<Customer | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false); // Add this line
  const IMAGEURL = process.env.REACT_APP_IMAGE_URL + "/customers/";
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch Customers from API
  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response: any = await getCustomers(searchText, page); // Pass searchText and page

      if (response?.data && Array.isArray(response.data)) {
        const sortedData = response.data
          .sort(
            (a: any, b: any) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )
          .map((customer: any, index: number) => ({
            ...customer,
            serialNumber: index + 1,
          }));

        setDataSource(sortedData);
        setFilteredData(sortedData); // Or apply search filtering separately
        setTotalItems(response.total || 0); // ✅ total items from backend
        setTotalPages(response.totalPages || 1); // ✅ total pages from backend
      } else {
        setDataSource([]);
        setFilteredData([]);
        setTotalItems(0);
        setTotalPages(1);
        message.error(response.message || "Failed to fetch customers");
      }
    } catch (error) {
      message.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [page, searchText]);

  // Handle search filtering
  useEffect(() => {
    const filtered = dataSource.filter((customer) => {
      const searchValue = searchText.toLowerCase();

      return (
        customer.name?.toLowerCase().includes(searchValue) ||
        "" ||
        customer.email?.toLowerCase().includes(searchValue) ||
        "" ||
        customer.mobile?.includes(searchText) ||
        "" ||
        customer.companyName?.toLowerCase().includes(searchValue) ||
        ""
      );
    });

    setFilteredData(filtered);
  }, [searchText, dataSource]);

  // Handle Edit Click

  // Handle Delete Confirmation
  const handleDeleteConfirmation = (id: string) => {
    setDeleteId(id);
  };
  // In the CustomersTable component:

  // Update the handleEdit function
  const handleEdit = (record: Customer) => {
    setCurrentTask(record); // Set the current task to edit
    setIsEditModalVisible(true); // Open the modal
    fetchCustomers();
  };

  // Handle Delete Action
  const handleDelete = async () => {
    if (deleteId) {
      try {
        const response: any = await deleteCustomer(deleteId);
        if (response) {
          message.success("Customer deleted successfully");
          fetchCustomers();
        } else {
          message.error("Failed to delete customer");
        }
      } catch (error) {
        message.error("Error deleting customer. Please try again.");
      } finally {
        setDeleteId(null);
      }
    }
  };

  const handleToggleStatus = async (
    customerId: string,
    currentStatus: boolean,
  ) => {
    try {
      const newStatus = !currentStatus;

      // Convert payload to FormData
      const formData = new FormData();
      formData.append("id", customerId);
      formData.append("isActive", String(newStatus));

      await editCustomer(formData); // Use FormData here

      message.success(
        `Customer ${newStatus ? "Activated" : "Deactivated"} successfully!`,
      );

      // Update local states
      setDataSource((prev) =>
        prev.map((role) =>
          role._id === customerId ? { ...role, isActive: newStatus } : role,
        ),
      );
      setFilteredData((prev) =>
        prev.map((role) =>
          role._id === customerId ? { ...role, isActive: newStatus } : role,
        ),
      );
    } catch (error) {
      console.error("Error updating customer status:", error);
      message.error("Failed to update customer status.");
    }
  };

  // Table Columns
  const columns = [
    {
      title: "#",
      dataIndex: "serialNumber",
      key: "serialNumber",
      render: (text: number) => text || "-",
    },
    {
      title: "Profile Photo",
      dataIndex: "profilePhoto",
      render: (text: string) => (
        <Image
          src={
            text
              ? `${IMAGEURL}${text}`
              : `${process.env.PUBLIC_URL}/download.png`
          }
          width={50}
          height={50}
          className="rounded-full"
          fallback={`${process.env.PUBLIC_URL}/download.png`}
          onError={(e) => {
            e.currentTarget.src = `${process.env.PUBLIC_URL}/download.png`;
          }}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text: string) => text || "-",
    },
    {
      title: "Mobile No.",
      dataIndex: "mobile",
      render: (text: string) => text || "-",
    },
    {
      title: "Company Name",
      dataIndex: "companyName",
      render: (text: string) => text || "-",
    },

    {
      title: "Email",
      dataIndex: "email",
      render: (text: string) => text || "-",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean, record: Customer) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <label
            style={{
              position: "relative",
              display: "inline-block",
              width: 40,
              height: 22,
              backgroundColor: isActive ? "#001529" : "#f0f0f0",
              borderRadius: 13,
              cursor: "pointer",
              transition: "background-color 0.3s ease",
              boxShadow: isActive
                ? "inset 0 0 0 2px #001529"
                : "inset 0 0 0 1px #ccc",
            }}
          >
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => handleToggleStatus(record._id, isActive)}
              style={{ display: "none" }}
            />
            <span
              style={{
                position: "absolute",
                width: 21,
                height: 19,
                left: isActive ? "calc(99% - 21px)" : 2,
                top: 1,
                backgroundColor: "#ffffff",
                borderRadius: "50%",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                transition: "left 0.3s ease, box-shadow 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: "bold",
                color: isActive ? "#001529" : "#666",
              }}
            >
              {isActive ? "ON" : "OFF"}
            </span>
          </label>
        </div>
      ),
    },

    ...(hasPermission("customers", "update") ||
    hasPermission("customers", "delete")
      ? [
          {
            title: "Action",
            dataIndex: "action",
            render: (_: any, record: Customer) => (
              <div
                className="d-flex"
                style={{ gap: "8px", alignItems: "center" }}
              >
                {hasPermission("customers", "update") && (
                  <Tooltip title="Edit">
                    <button
                      onClick={() => handleEdit(record)}
                      className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
               shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
               hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
               active:translate-y-[1px] active:shadow-none 
               text-white flex items-center justify-center 
               transition-all duration-200 ease-in-out group"
                    >
                      <FaPen
                        className="group-hover:scale-110 transition-transform duration-200"
                        size={12}
                      />
                    </button>
                  </Tooltip>
                )}

                {/* Delete Icon Button */}
                {hasPermission("customers", "delete") && (
                  <Tooltip title="Delete">
                    <button
                      onClick={() => handleDeleteConfirmation(record._id)}
                      className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 
               shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
               hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
               active:translate-y-1 active:shadow-none 
               text-white flex items-center justify-center 
               transition-all duration-200 group"
                    >
                      <FaTrashAlt
                        className="group-hover:scale-110 transition-transform"
                        size={12}
                      />
                    </button>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  return (
    <div>
      {/* Header & Search */}
      <Row
        className="m-2"
        align="middle"
        justify="space-between"
        gutter={[16, 16]}
      >
        {/* Left Side - Heading */}
        <Col xs={24} sm={24} md={6} lg={6} xl={6}>
          <h2 className="headername">Customers</h2>
        </Col>

        {/* Right Side - Search + Button in one row */}
        <Col xs={24} md={10}>
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            {/* Search Input (Flexible Width) */}
            <Input
              prefix={<SearchOutlined />}
              size="large"
              placeholder="Search..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="flex-1"
              style={{ width: 200, height: "33px" }}
            />

            {/* Add Customers Button */}
            <Button
              type="primary"
              onClick={() => setIsAddTaskModalOpen(true)}
              className="allbuttons"
            >
              <RiAddBoxFill className="button-icon" />
              <span className="button-text">Add</span>
            </Button>

            <AddCustomerModal
              visible={isAddTaskModalOpen}
              onClose={() => setIsAddTaskModalOpen(false)}
              onTaskCreated={() => {
                setIsAddTaskModalOpen(false);
                fetchCustomers();
              }}
            />
          </div>
        </Col>
      </Row>
      {/* Customers Table - Fully Responsive */}
      <Row justify="center">
        <Col xs={24}>
          <Card className=" w-100">
            <Spin spinning={loading}>
              <div
                className="scrollbar-hide custom-scroll-table"
                style={{ overflowX: "auto" }}
              >
                <Table
                  columns={columns}
                  dataSource={filteredData}
                  rowKey="_id"
                  scroll={{ x: "max-content" }}
                  pagination={{
                    pageSize: 10, // Number of items per page
                    current: page, // Current page number
                    onChange: (pageNumber) => setPage(pageNumber), // Update page state on change
                    showSizeChanger: false, // Hide the page size changer
                    total: totalItems, // Total number of items
                  }}
                />
              </div>
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteId} // <-- This makes the modal open when deleteId has a value
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        // footer={null}
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this customer?</p>
      </Modal>

      {currentTask && (
        // In your parent component where you render EditCustomersModal:
        <EditCustomersModal
          visible={isEditModalVisible}
          customerId={currentTask?._id} // Make sure this is passed correctly
          onClose={() => setIsEditModalVisible(false)}
          onSuccess={() => {
            setIsEditModalVisible(false);
            fetchCustomers();
          }}
        />
      )}
    </div>
  );
};

export const CUSTOMER_TABLE_COLUMNS = [
  "profilePhoto",
  "name",
  "mobile",
  "companyName",
  "email",
  "isActive",
];
// =====================================================
